import { DialogComponent } from '@theme/dialog';
import { isMobileBreakpoint, isTouchDevice } from '@theme/utilities';

/**
 * Bild-Hotspot Punkt
 * - Mobil / Touch: oeffnet ein Bottom-Sheet von unten (modal, wie Quick-Add)
 * - Desktop: oeffnet ein Popover direkt am Punkt
 */
class BildHotspotPunkt extends DialogComponent {
  requiredRefs = ['trigger', 'dialog'];

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removePopoverListeners();
  }

  get #isSheetMode() {
    return isMobileBreakpoint() || isTouchDevice();
  }

  handleHotspotClick = () => {
    const { dialog } = this.refs;

    if (dialog.open) {
      this.closeHotspot();
      return;
    }

    this.#closeOthers();

    if (this.#isSheetMode) {
      dialog.dataset.mode = 'sheet';
      this.showDialog();
    } else {
      dialog.dataset.mode = 'popover';
      this.#showPopover();
    }
  };

  closeHotspot = () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    if (dialog.dataset.mode === 'popover') {
      this.#closePopover();
    } else {
      this.closeDialog();
    }
  };

  #closeOthers() {
    document.querySelectorAll('bild-hotspot-punkt').forEach((punkt) => {
      if (punkt !== this && punkt instanceof BildHotspotPunkt) {
        punkt.closeHotspot();
      }
    });
  }

  #showPopover = () => {
    const { dialog } = this.refs;
    this.#calculatePlacement();
    dialog.dataset.showing = 'true';
    dialog.show();
    document.body.addEventListener('click', this.#lightDismissMouse);
    document.body.addEventListener('keydown', this.#lightDismissKeyboard);
  };

  #closePopover = async () => {
    const { dialog } = this.refs;
    this.#removePopoverListeners();
    dialog.dataset.closing = 'true';

    const animations = dialog.getAnimations({ subtree: true });
    await Promise.allSettled(animations.map((animation) => animation.finished));

    dialog.close();
    delete dialog.dataset.showing;
    delete dialog.dataset.closing;
    delete dialog.dataset.placement;
  };

  #removePopoverListeners = () => {
    document.body.removeEventListener('click', this.#lightDismissMouse);
    document.body.removeEventListener('keydown', this.#lightDismissKeyboard);
  };

  #lightDismissMouse = (event) => {
    if (event.composedPath().includes(this)) return;
    this.closeHotspot();
  };

  #lightDismissKeyboard = (event) => {
    if (event.key === 'Escape') this.closeHotspot();
  };

  #calculatePlacement() {
    const { trigger, dialog } = this.refs;
    const container = this.closest('[data-bild-hotspot-media]');
    if (!container) return;

    const GAP = 12;

    const containerRect = container.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();

    // Dialog unsichtbar anzeigen, um seine Masse zu messen
    dialog.style.visibility = 'hidden';
    dialog.style.display = 'block';
    const { width: dialogWidth, height: dialogHeight } = dialog.getBoundingClientRect();
    dialog.style.removeProperty('display');
    dialog.style.removeProperty('visibility');

    const buttonLeft = triggerRect.left - containerRect.left;
    const buttonRight = triggerRect.right - containerRect.left;
    const buttonTop = triggerRect.top - containerRect.top;
    const buttonBottom = triggerRect.bottom - containerRect.top;

    const spaceRight = containerRect.width - buttonRight - GAP;
    const spaceLeft = buttonLeft - GAP;

    let x = 'center';
    if (spaceRight >= dialogWidth + GAP) {
      x = 'right';
    } else if (spaceLeft >= dialogWidth + GAP) {
      x = 'left';
    }

    let y = 'bottom';
    let offset = 0;

    if (x === 'center') {
      const spaceBelow = containerRect.height - buttonBottom;
      y = spaceBelow >= dialogHeight + GAP * 2 || buttonTop < containerRect.height / 2 ? 'bottom' : 'top';
    } else if (buttonTop + dialogHeight > containerRect.height - GAP) {
      // Unterkante am Punkt ausrichten, bei Bedarf nach unten schieben
      y = 'top';
      const startY = buttonBottom - dialogHeight;
      if (startY < GAP) offset = GAP - startY;
    }

    dialog.dataset.placement = `${x},${y}`;

    if (offset !== 0) {
      dialog.style.setProperty('--bh-popover-offset', `${offset}px`);
    } else {
      dialog.style.removeProperty('--bh-popover-offset');
    }
  }
}

if (!customElements.get('bild-hotspot-punkt')) {
  customElements.define('bild-hotspot-punkt', BildHotspotPunkt);
}

// Theme-Editor: Popup oeffnen, wenn der Block ausgewaehlt wird
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:block:select', (event) => {
    const target = event.target instanceof Element ? event.target.closest('bild-hotspot-punkt') : null;
    if (target instanceof BildHotspotPunkt && !target.refs.dialog.open) {
      target.handleHotspotClick();
    }
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const target = event.target instanceof Element ? event.target.closest('bild-hotspot-punkt') : null;
    if (target instanceof BildHotspotPunkt) {
      target.closeHotspot();
    }
  });
}
